import React, {
  useEffect,
  useState,
  useCallback,
  useLayoutEffect,
} from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Col,
  Row,
  Card,
  CardBody,
  Input,
  ModalHeader,
  ModalBody,
  Label,
  ModalFooter,
  Modal,
  Form,
  FormFeedback,
  UncontrolledDropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
} from 'reactstrap';
import FeatherIcon from 'feather-icons-react';
import moment from 'moment';
import BreadCrumb from '../../Components/Common/BreadCrumb';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';

import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { removeFile, uploadFile } from 'services/fileService';
import {
  addNewOrganization,
  editNewOrganization,
  exportOrganizations,
  getAllOrganization,
  removeOrg,
  uploadOrganizations,
} from 'services/orgService';
import { useDebounce } from '../../hooks/useDebounce';
import { useDispatch } from 'react-redux';
import { isVisible } from 'slices/loader/reducer';
import ConfirmDialog from 'Components/Common/ConfirmDialog';
import { indianCities, indianStates } from 'utility/constant';
import { FilePreview } from 'Components/Common/FilePreview';
import OrganizationDetailsModal from './organization-details-modal';
import logo from '../../assets/images/Logo.png';
import { uploadFieldCategoryMap } from 'Components/constants/uploadFieldCategoryMap';
import BulkExcelActions, {
  BulkExcelLoadingOverlay,
} from 'Components/Common/BulkExcelActions';
import { useBulkExcelOperations } from 'utility/excelBulkOperations';

type UploadedFile = {
  url: string;
  uploadedAt: number;
};

type UploadField =
  'misaalShareef' | 'directivesAlVazaratUsSaifiyah' | 'mazaarPropertyMap';

const Organizations = () => {
  const [isEdit, setIsEdit] = useState(false);
  const [orgs, setOrgs] = useState([]);
  const [deleteOrg, setDeleteOrg] = useState(null);
  const [editOrg, setEditOrg] = useState(null);
  const [modal, setModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [detailsModal, setDetailsModal] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [uploads, setUploads] = useState<Record<UploadField, UploadedFile[]>>({
    misaalShareef: [],
    directivesAlVazaratUsSaifiyah: [],
    mazaarPropertyMap: [],
  });

  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const toggle = useCallback(() => {
    if (modal) {
      setModal(false);
      setIsEdit(false);
      setEditOrg(null);
    } else {
      setModal(true);
    }
  }, [modal]);

  const toggleDetailsModal = () => {
    setDetailsModal(prev => !prev);
    if (detailsModal) setSelectedOrg(null);
  };

  // Yup Validation Schema
  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Please Enter Mazar Name'),
    region: Yup.string().required('Please Select State'),
    city: Yup.string().required('Please Select City'),
    address: Yup.string().required('Please Enter Address'),
  });

  // React Hook Form
  const {
    control,
    handleSubmit,
    reset,
    getValues,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      name: '',
      region: '',
      city: '',
      address: '',
    },
  });

  const handleMultiUpload = async (
    files: FileList | null,
    field: UploadField,
  ) => {
    if (!files || !files.length) return;
    dispatch(isVisible(true));
    try {
      const fileArr = Array.from(files);
      const values = getValues();

      const results = await Promise.all(
        fileArr.map(file =>
          uploadFile({
            file,

            category: uploadFieldCategoryMap[field],

            customFileName: field,

            organizationName: values.name,
          }),
        ),
      );

      const newFiles: UploadedFile[] = results.map(r => ({
        url: r.data.url,
        uploadedAt: Date.now(),
      }));

      setUploads(prev => {
        const updated = [...newFiles, ...prev[field]].sort(
          (a, b) => b.uploadedAt - a.uploadedAt,
        );
        setValue(field as any, updated.map(f => f.url).join(','));
        return { ...prev, [field]: updated };
      });
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message, {
        autoClose: 2000,
      });
    } finally {
      dispatch(isVisible(false));
    }
  };

  const removeUploadedFile = async (field: UploadField, index: number) => {
    const file = uploads[field][index];
    dispatch(isVisible(true));
    try {
      setUploads(prev => {
        const updated = prev[field].filter((_, i) => i !== index);
        setValue(field as any, updated.map(f => f.url).join(','));
        return { ...prev, [field]: updated };
      });

      if (file?.url) {
        await removeFile([file.url]);
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message, {
        autoClose: 2000,
      });
    } finally {
      dispatch(isVisible(false));
    }
  };

  const loadData = async (search = '') => {
    dispatch(isVisible(true));
    try {
      const authUser = sessionStorage.getItem('authUser');
      if (authUser) {
        const parsedUser = JSON.parse(authUser);
        const result = await getAllOrganization(null, search, parsedUser.id);
        if (result?.data?.rows?.length) {
          const list = result?.data?.rows?.map(org => org);
          setOrgs(list);
        } else {
          setOrgs([]);
        }
      }
    } catch (error) {
      console.error('Error fetching Mazar:', error);
      toast.error(`Error fetching Mazar: ${error}`, {
        autoClose: 2000,
      });
      setOrgs([]);
    } finally {
      dispatch(isVisible(false));
    }
  };

  const { loading, handleExport, handleUpload } = useBulkExcelOperations({
    exportFile: exportOrganizations,
    getExportFilename: () => 'Mazars.xlsx',
    uploadFile: uploadOrganizations,
    onUploadSuccess: () => loadData(),
  });

  useEffect(() => {
    loadData(debouncedSearchQuery);
  }, [debouncedSearchQuery]);

  useLayoutEffect(() => {
    loadData();
  }, []);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const onSubmit = async values => {
    dispatch(isVisible(true));
    const newLead = {
      organization: values.name,
      state: values.region,
      city: values.city,
      address: values.address,
      misaalShareef: uploads.misaalShareef.map(f => f.url).join(',') || null,
      directivesAlVazaratUsSaifiyah:
        uploads.directivesAlVazaratUsSaifiyah.map(f => f.url).join(',') || null,
      mazaarPropertyMap:
        uploads.mazaarPropertyMap.map(f => f.url).join(',') || null,
    };
    try {
      if (isEdit) {
        const result = await editNewOrganization(editOrg.id, newLead);

        if (result?.data)
          toast.success('Org Updated Successfully', { autoClose: 2000 });
      } else {
        const result = await addNewOrganization(newLead);

        if (result?.data)
          toast.success('Org Added Successfully', { autoClose: 2000 });
      }
    } catch (e) {
      toast.error(`Error in onSubmit in Organization: ${e}`, {
        autoClose: 2000,
      });
    } finally {
      dispatch(isVisible(false));
    }
    loadData();
    toggle();
    reset();
    setUploads({
      misaalShareef: [],
      directivesAlVazaratUsSaifiyah: [],
      mazaarPropertyMap: [],
    });
  };

  const onClickDelete = org => {
    setDeleteOrg(org);
    setDeleteModal(true);
  };

  const onClickEdit = org => {
    setEditOrg(org);
    setIsEdit(true);
    setModal(true);
  };

  const handleDeleteLead = async () => {
    dispatch(isVisible(true));
    try {
      if (deleteOrg) {
        await removeOrg(deleteOrg?.id);
        await loadData();
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message, {
        autoClose: 2000,
      });
    } finally {
      dispatch(isVisible(false));
    }
    setDeleteOrg(null);
    setDeleteModal(false);
  };

  useEffect(() => {
    if (editOrg) {
      reset({
        name: editOrg.organization || '',
        region: editOrg.state ? String(editOrg.state).toUpperCase() : '',
        city: editOrg.city ? String(editOrg.city).toUpperCase() : '',
        address: editOrg.address || '',
      });

      const toFiles = (v: any) =>
        (v ? String(v).split(',').filter(Boolean) : []).map((url: string) => ({
          url,
          uploadedAt: Date.now(),
        }));
      setUploads({
        misaalShareef: toFiles(editOrg.misaalShareef),
        directivesAlVazaratUsSaifiyah: toFiles(
          editOrg.directivesAlVazaratUsSaifiyah,
        ),
        mazaarPropertyMap: toFiles(editOrg.mazaarPropertyMap),
      });
    } else {
      reset({
        name: '',
        region: '',
        city: '',
        address: '',
      });
      setUploads({
        misaalShareef: [],
        directivesAlVazaratUsSaifiyah: [],
        mazaarPropertyMap: [],
      });
    }
  }, [editOrg, reset]);

  return (
    <React.Fragment>
      {loading && <BulkExcelLoadingOverlay />}
      <div className="page-content">
        <ToastContainer closeButton={false} limit={1} />
        <ConfirmDialog
          isOpen={deleteModal}
          title="Confirm Delete"
          message="Are you sure you want to delete this mazar?"
          confirmLabel="Yes, Delete"
          confirmBtnColor="danger"
          iconClass="ri-delete-bin-line text-danger"
          onConfirm={handleDeleteLead}
          onCancel={() => setDeleteModal(false)}
        />
        <BreadCrumb
          title="Mazaraat"
          breadcrumbItems={[
            { label: 'Dashboard', path: '/dashboard' },
            { label: 'Mazaraat', isActive: true },
          ]}
        />
        <Row className="g-4 mb-3">
          <div className="col-sm-auto">
            <div className="mb-3">
              <div className="mb-2">
                <button
                  type="button"
                  className="btn btn-primary add-btn"
                  onClick={() => {
                    setIsEdit(false);
                    toggle();
                  }}
                >
                  <i className="ri-add-line align-bottom me-1"></i> Add Mazar
                </button>
              </div>

              <BulkExcelActions
                helpTargetId="organizations-bulk-upload-help"
                onDownload={handleExport}
                onUpload={handleUpload}
                helpClassName="text-muted fw-medium mb-2"
                disabled={loading}
                downloadDisabled={loading || orgs.length < 1}
              />
            </div>
          </div>
          <div className="col-sm-3 ms-auto">
            <div className="d-flex justify-content-sm-end gap-2">
              <div className="search-box ms-2 col-sm-10">
                <Input
                  type="text"
                  className="form-control"
                  placeholder="Search Mazar"
                  value={searchQuery}
                  onChange={handleSearch}
                />
                <i className="ri-search-line search-icon"></i>
              </div>
            </div>
          </div>
        </Row>
        <div className="row">
          {orgs.map((item, key) => (
            <Col xxl={4} sm={6} key={key} className="project-card">
              <Link
                to={`/organizations/${item.id}/choose-category?mazar_name=${item.organization}`}
                className="text-decoration-none text-reset"
              >
                <Card className="card-height-100">
                  <CardBody className="d-flex flex-column h-100">
                    <div className="d-flex flex-column">
                      <div className="d-flex">
                        <div className="flex-grow-1">
                          <p className="text-muted mb-4"></p>
                        </div>
                        <div className="flex-shrink-0">
                          <div className="d-flex gap-1 align-items-center">
                            <UncontrolledDropdown
                              direction="start"
                              onMouseEnter={() => setHoveredId(item.id)}
                              onMouseLeave={() => setHoveredId(null)}
                              isOpen={hoveredId === item.id}
                              toggle={() => null}
                            >
                              <DropdownToggle
                                tag="button"
                                className="btn btn-link text-muted p-1 mt-n2 py-0 text-decoration-none fs-15 material-shadow-none"
                                onClick={e => e.stopPropagation()}
                              >
                                <FeatherIcon
                                  icon="more-horizontal"
                                  className="icon-sm"
                                />
                              </DropdownToggle>

                              <DropdownMenu className="dropdown-menu-end">
                                <DropdownItem
                                  onClick={e => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setSelectedOrg(item);
                                    setDetailsModal(true);
                                  }}
                                >
                                  <i className="ri-eye-line align-bottom me-2 text-muted"></i>{' '}
                                  View Details
                                </DropdownItem>
                                <DropdownItem
                                  onClick={e => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    navigate(
                                      '/organizations/' +
                                        item.id +
                                        '/properties?mazar_name=' +
                                        item.organization,
                                    );
                                  }}
                                >
                                  <i className="ri-store-line align-bottom me-2 text-muted"></i>{' '}
                                  Manage Properties
                                </DropdownItem>
                                <DropdownItem
                                  onClick={e => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    navigate(
                                      '/organizations/' + item.id + '/users',
                                    );
                                  }}
                                >
                                  <i className="ri-group-line align-bottom me-2 text-muted"></i>{' '}
                                  Manage Users
                                </DropdownItem>
                                <DropdownItem
                                  onClick={e => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onClickEdit(item);
                                  }}
                                >
                                  <i className="ri-pencil-fill align-bottom me-2 text-muted"></i>{' '}
                                  Edit Mazar
                                </DropdownItem>
                                <div className="dropdown-divider"></div>
                                <DropdownItem
                                  onClick={e => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onClickDelete(item);
                                  }}
                                  data-bs-toggle="modal"
                                  data-bs-target="#removeProjectModal"
                                >
                                  <i className="ri-delete-bin-fill align-bottom me-2 text-muted"></i>{' '}
                                  Remove
                                </DropdownItem>
                              </DropdownMenu>
                            </UncontrolledDropdown>
                          </div>
                        </div>
                      </div>
                      <div className="d-flex align-items-center mb-2">
                        <div className="flex-shrink-0 me-3">
                          <div className="avatar-md">
                            <span>
                              <img
                                src={item.logo || logo}
                                alt="organization logo"
                                className="img-fluid p-1"
                              />
                            </span>
                          </div>
                        </div>
                        <div className="flex-grow-1 overflow-hidden">
                          <h5 className="mb-1 fs-15 text-truncate">
                            <Link
                              to={
                                '/organizations/' +
                                item.id +
                                '/choose-category?mazar_name=' +
                                item.organization
                              }
                              className="text-body"
                              title={item.organization}
                            >
                              {item.organization}
                            </Link>
                          </h5>
                          <p className="text-muted text-truncate-two-lines mb-3">
                            {item.address}
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardBody>
                  <div className="card-footer bg-transparent border-top-dashed py-2 overflow-hidden">
                    <div className="d-flex align-items-center">
                      <div className="flex-grow-1 overflow-hidden">
                        <div
                          className="text-muted text-truncate"
                          title={`${item.city}, ${item.state}`}
                        >
                          {item.city}, {item.state}
                        </div>
                      </div>
                      <div className="flex-shrink-0">
                        <div className="text-muted">
                          <i className="ri-calendar-event-fill me-1 align-bottom"></i>{' '}
                          {moment(new Date(item.createdAt)).format('DD MMM Y')}
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              </Link>
            </Col>
          ))}
        </div>
        <Modal id="showModal" isOpen={modal} toggle={toggle} centered>
          <ModalHeader className="bg-light p-3" toggle={toggle}>
            {isEdit ? 'Edit Mazar' : 'Add Mazar'}
          </ModalHeader>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <ModalBody className="overflow-hidden">
              <Row className="g-3">
                <Col lg={12}>
                  <Label htmlFor="name-field" className="form-label">
                    Mazar Name *
                  </Label>
                  <Controller
                    name="name"
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        id="name-field"
                        className="form-control"
                        placeholder="Enter Name"
                        invalid={!!errors.name}
                      />
                    )}
                  />
                  {errors.name && (
                    <FormFeedback>{errors.name.message}</FormFeedback>
                  )}
                </Col>
                <Col lg={6}>
                  <Label htmlFor="region-field" className="form-label">
                    State *
                  </Label>
                  <Controller
                    name="region"
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        type="select"
                        id="region-field"
                        className="form-control"
                        invalid={!!errors.region}
                      >
                        <option value="">Select State</option>
                        {indianStates.map(state => (
                          <option key={state} value={state.toUpperCase()}>
                            {state.toUpperCase()}
                          </option>
                        ))}
                      </Input>
                    )}
                  />
                  {errors.region && (
                    <FormFeedback>{errors.region.message}</FormFeedback>
                  )}
                </Col>
                <Col lg={6}>
                  <Label htmlFor="city-field" className="form-label">
                    City *
                  </Label>
                  <Controller
                    name="city"
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        type="select"
                        id="city-field"
                        className="form-control"
                        invalid={!!errors.city}
                      >
                        <option value="">Select City</option>
                        {indianCities.map(city => (
                          <option key={city} value={city.toUpperCase()}>
                            {city.toUpperCase()}
                          </option>
                        ))}
                      </Input>
                    )}
                  />
                  {errors.city && (
                    <FormFeedback>{errors.city.message}</FormFeedback>
                  )}
                </Col>
                <Col lg={12}>
                  <Label htmlFor="address-field" className="form-label">
                    Address *
                  </Label>
                  <Controller
                    name="address"
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        id="address-field"
                        className="form-control"
                        placeholder="Enter Address"
                        invalid={!!errors.address}
                      />
                    )}
                  />
                  {errors.address && (
                    <FormFeedback>{errors.address.message}</FormFeedback>
                  )}
                </Col>

                <Col lg={12}>
                  <hr className="my-2" />
                  <h6 className="mb-2">Documents</h6>
                </Col>

                <Col lg={12}>
                  <Label className="form-label">Misaal Shareef</Label>
                  <Input
                    type="file"
                    multiple
                    onChange={e =>
                      handleMultiUpload(e.target.files, 'misaalShareef')
                    }
                  />
                  <FilePreview
                    files={uploads.misaalShareef}
                    onRemove={i => removeUploadedFile('misaalShareef', i)}
                  />
                </Col>

                <Col lg={12}>
                  <Label className="form-label">
                    Directives (Al Vazarat Us Saifiyah)
                  </Label>
                  <Input
                    type="file"
                    multiple
                    onChange={e =>
                      handleMultiUpload(
                        e.target.files,
                        'directivesAlVazaratUsSaifiyah',
                      )
                    }
                  />
                  <FilePreview
                    files={uploads.directivesAlVazaratUsSaifiyah}
                    onRemove={i =>
                      removeUploadedFile('directivesAlVazaratUsSaifiyah', i)
                    }
                  />
                </Col>

                <Col lg={12}>
                  <Label className="form-label">Mazar Property Map</Label>
                  <Input
                    type="file"
                    multiple
                    onChange={e =>
                      handleMultiUpload(e.target.files, 'mazaarPropertyMap')
                    }
                  />
                  <FilePreview
                    files={uploads.mazaarPropertyMap}
                    onRemove={i => removeUploadedFile('mazaarPropertyMap', i)}
                  />
                </Col>
              </Row>
            </ModalBody>
            <ModalFooter>
              <div className="hstack gap-2 justify-content-end">
                <button
                  type="button"
                  className="btn btn-light"
                  onClick={toggle}
                >
                  Close
                </button>
                <button type="submit" className="btn btn-success">
                  {isEdit ? 'Update' : 'Create'}
                </button>
              </div>
            </ModalFooter>
          </Form>
        </Modal>

        <OrganizationDetailsModal
          isOpen={detailsModal}
          toggle={toggleDetailsModal}
          data={selectedOrg}
        />
      </div>
    </React.Fragment>
  );
};

export default Organizations;
